// ============================================================================
// oidc.ts — direct-Keycloak (authMode 'oidc') Authorization-Code + PKCE flow.
//
// Framework-free on purpose: the HTTP interceptor and the Connect transport
// read the access token via getAccessToken() WITHOUT importing AuthService,
// so there's no DI/import cycle (AuthService → StaffApi → ticketing client).
//
// Tokens live in sessionStorage (cleared on tab close). The PKCE verifier +
// state are transient and removed as soon as the code is exchanged.
// ============================================================================
import type { OidcConfig } from './app-config';

// Storage namespace. Under path-routing every tenant's console shares ONE browser
// origin (app-staging.lumendesk.io/<slug>), so token storage must be namespaced
// by the base path or tenant A and tenant B would clobber each other's session.
// Derived from the injected <base href> (document.baseURI): "/acme/" → "acme.";
// a root-hosted console ("/") → "" (unprefixed, back-compatible with existing
// host-routed sessions).
function storageNs(): string {
  try {
    if (typeof document === 'undefined' || !document.baseURI) return '';
    const p = new URL(document.baseURI).pathname.replace(/^\/+|\/+$/g, '');
    return p ? p.replace(/[^a-z0-9-]/gi, '_') + '.' : '';
  } catch {
    return '';
  }
}

const NS = storageNs();
const K = {
  access: `ld.oidc.${NS}access_token`,
  id: `ld.oidc.${NS}id_token`,
  refresh: `ld.oidc.${NS}refresh_token`,
  exp: `ld.oidc.${NS}expires_at`, // epoch ms
  verifier: `ld.oidc.${NS}pkce_verifier`,
  state: `ld.oidc.${NS}state`,
  returnTo: `ld.oidc.${NS}return_to`,
} as const;

/** Treat a token as expired this many ms early, to cover clock skew + latency. */
const SKEW_MS = 15_000;

export interface OidcTokens {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
}

function store(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Current access token if present and not (nearly) expired; else null. */
export function getAccessToken(): string | null {
  const s = store();
  if (!s) return null;
  const t = s.getItem(K.access);
  if (!t) return null;
  const exp = Number(s.getItem(K.exp) || 0);
  if (exp && Date.now() > exp - SKEW_MS) return null;
  return t;
}

export function getIdToken(): string | null {
  return store()?.getItem(K.id) ?? null;
}

export function getRefreshToken(): string | null {
  return store()?.getItem(K.refresh) ?? null;
}

/** ms until the access token expires (negative if already past); 0 when unknown. */
export function msUntilExpiry(): number {
  const exp = Number(store()?.getItem(K.exp) || 0);
  return exp ? exp - Date.now() : 0;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchange the stored refresh token for a fresh access token (Keycloak
 * `grant_type=refresh_token`, public client — no secret). Serialized so
 * concurrent callers share a single round trip: refresh-token rotation makes
 * each token single-use, and parallel refreshes would invalidate the session.
 * On a hard failure (refresh token spent/expired) the refresh token is dropped
 * so we stop retrying. Returns true when a new access token was stored.
 */
export function refreshTokens(cfg: OidcConfig): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const rt = getRefreshToken();
  if (!rt) return Promise.resolve(false);
  refreshInFlight = (async () => {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: rt,
      client_id: cfg.clientId,
    });
    try {
      const res = await fetch(`${authBase(cfg)}/token`, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) {
        store()?.removeItem(K.refresh);
        return false;
      }
      storeTokens((await res.json()) as OidcTokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export function storeTokens(t: OidcTokens): void {
  const s = store();
  if (!s || !t?.access_token) return;
  s.setItem(K.access, t.access_token);
  if (t.id_token) s.setItem(K.id, t.id_token);
  if (t.refresh_token) s.setItem(K.refresh, t.refresh_token);
  const ttlMs = (t.expires_in ?? 300) * 1000;
  s.setItem(K.exp, String(Date.now() + ttlMs));
}

export function clearTokens(): void {
  const s = store();
  if (!s) return;
  for (const k of [K.access, K.id, K.refresh, K.exp, K.verifier, K.state, K.returnTo]) {
    s.removeItem(k);
  }
}

// --- PKCE helpers -----------------------------------------------------------

function base64Url(bytes: ArrayBuffer): string {
  let bin = '';
  for (const b of new Uint8Array(bytes)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(bytes = 48): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return base64Url(a.buffer);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(digest);
}

// --- flow -------------------------------------------------------------------

/**
 * Build the Keycloak authorize URL and persist the PKCE verifier + state.
 * `returnTo` is the in-app path to land on after the round trip.
 */
export async function beginAuth(cfg: OidcConfig, returnTo: string): Promise<string> {
  const verifier = randomString(48);
  const state = randomString(24);
  const s = store();
  if (s) {
    s.setItem(K.verifier, verifier);
    s.setItem(K.state, state);
    s.setItem(K.returnTo, returnTo || '/');
  }
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: cfg.scope,
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });
  return `${authBase(cfg)}/auth?${params.toString()}`;
}

export interface CompleteResult {
  ok: boolean;
  returnTo: string;
  error?: string;
}

/** Exchange the authorization code (PKCE) for tokens. Called from the callback. */
export async function completeAuth(cfg: OidcConfig, query: URLSearchParams): Promise<CompleteResult> {
  const s = store();
  const returnTo = s?.getItem(K.returnTo) || '/';
  const err = query.get('error');
  if (err) {
    clearTransient();
    return { ok: false, returnTo, error: query.get('error_description') || err };
  }
  const code = query.get('code');
  const state = query.get('state');
  const expectedState = s?.getItem(K.state) ?? null;
  const verifier = s?.getItem(K.verifier) ?? null;
  if (!code || !state || !verifier || state !== expectedState) {
    clearTransient();
    return { ok: false, returnTo, error: 'invalid_callback_state' };
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: cfg.redirectUri,
    client_id: cfg.clientId,
    code_verifier: verifier,
  });
  let res: Response;
  try {
    res = await fetch(`${authBase(cfg)}/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (e) {
    clearTransient();
    return { ok: false, returnTo, error: 'token_endpoint_unreachable' };
  }
  clearTransient();
  if (!res.ok) {
    return { ok: false, returnTo, error: `token_exchange_${res.status}` };
  }
  storeTokens((await res.json()) as OidcTokens);
  return { ok: true, returnTo };
}

/** End-session (RP-initiated logout) URL with id_token_hint + post-logout redirect. */
export function buildLogoutUrl(cfg: OidcConfig): string {
  const params = new URLSearchParams({
    post_logout_redirect_uri: cfg.postLogoutRedirectUri,
    client_id: cfg.clientId,
  });
  const id = getIdToken();
  if (id) params.set('id_token_hint', id);
  return `${authBase(cfg)}/logout?${params.toString()}`;
}

// --- claims -----------------------------------------------------------------

export interface JwtClaims {
  sub?: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
  [k: string]: unknown;
}

export function decodeJwt(token: string): JwtClaims | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    // Handle UTF-8 payloads correctly.
    const decoded = decodeURIComponent(
      Array.from(json, (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''),
    );
    return JSON.parse(decoded) as JwtClaims;
  } catch {
    return null;
  }
}

/** All role names from the token (realm + every client in resource_access). */
export function rolesFromClaims(claims: JwtClaims): string[] {
  const realm = claims.realm_access?.roles ?? [];
  const resource = Object.values(claims.resource_access ?? {}).flatMap((r) => r.roles ?? []);
  return [...realm, ...resource];
}

// --- internals --------------------------------------------------------------

function authBase(cfg: OidcConfig): string {
  // Keycloak: <issuer>/protocol/openid-connect/{auth,token,logout}
  return `${cfg.issuer.replace(/\/$/, '')}/protocol/openid-connect`;
}

function clearTransient(): void {
  const s = store();
  if (!s) return;
  s.removeItem(K.verifier);
  s.removeItem(K.state);
  s.removeItem(K.returnTo);
}
